import { FC, useEffect, useState, useMemo } from 'react';
import { StudentsViewProps } from './students.interfaces';
import { Grid } from '@mui/material';
import { pages } from '../../../utils/common';
import MainTitle from '../../components/mainTitle';
import TabGroup from '../../components/tabGroup';
import NavigationDrawer from '../../components/navigationDrawer/navigationDrawer';
import { Helmet } from 'react-helmet';
import { CONSTANTS } from '../../../utils/CONSTS';
import StudentListFilters from './subviews/components/studentListFilters';
import { useNavigate, useParams, useLocation, Outlet, Navigate } from 'react-router-dom';
import {
  useStudentFiltersState,
  useStudentFiltersActions,
} from './contexts/studentFilters.context';
import { useStudentTabMenuItems } from './components/StudentTabMenuItems';
import { StudentsProvider } from './contexts/students.context';
import { useTranslation } from 'src/hooks/useTranslationHook';
import { StudentListProvider } from './contexts/studentList.context';
import { PermissionGate } from '../../../components/rbac/PermissionGate';
import { useRbacState } from 'src/context/rbac.context';
import { useRbacSafeLandingPath } from 'src/hooks/useRbacSafeLandingPath';
import { hasReadPermission } from 'src/utils/rbacHelpers';

// Create a separate component for the content that needs access to the filters context
const StudentsContent: FC = () => {
  const [selectedTab, setSelectedTab] = useState(0);
  const { studentSubRouteId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const filters = useStudentFiltersState();
  const { updateFilters } = useStudentFiltersActions();
  const { translateText } = useTranslation();
  const studentTabs = useStudentTabMenuItems(filters, translateText);

  const { rbac } = useRbacState();
  const safeLanding = useRbacSafeLandingPath();

  // Don't run navigation logic if we're on a details route
  const isDetailsRoute = location.pathname.includes('/details');

  const availableRouteIds = useMemo(() => {
    const routes: string[] = [];
    if (hasReadPermission(rbac, 'students', 'studentList')) {
      routes.push('student-list');
    }
    if (hasReadPermission(rbac, 'students', 'attendance')) {
      routes.push('attendance');
    }
    if (hasReadPermission(rbac, 'students', 'fees')) {
      routes.push('fees');
    }
    if (hasReadPermission(rbac, 'students', 'events')) {
      routes.push('events');
    }
    if (hasReadPermission(rbac, 'students', 'scores')) {
      routes.push('scores');
    }
    if (hasReadPermission(rbac, 'students', 'documents')) {
      routes.push('documents');
    }
    return routes;
  }, [rbac]);

  useEffect(() => {
    // Don't run navigation logic if we're on a details route
    if (isDetailsRoute) {
      return;
    }
    if (availableRouteIds.length === 0) {
      return;
    }


    const routeId =
      studentSubRouteId && studentSubRouteId !== 'undefined'
        ? studentSubRouteId
        : availableRouteIds[0];

    if (!availableRouteIds.includes(routeId)) {
      navigate(pages.students.path + '/' + availableRouteIds[0], {
        replace: true,
      });
      return;
    }

    const filteredTabIndex = availableRouteIds.indexOf(routeId);
    setSelectedTab(filteredTabIndex >= 0 ? filteredTabIndex : 0);

    if (!studentSubRouteId || studentSubRouteId === 'undefined') {
      navigate(pages.students.path + '/' + routeId, {
        replace: true,
      });
    }
  }, [studentSubRouteId, navigate, availableRouteIds, isDetailsRoute]);

  const locationChangeHandler = (value: string | number) => {
    const tabIndex = value as number;
    setSelectedTab(tabIndex);
    if (tabIndex >= 0 && tabIndex < availableRouteIds.length) {
      const routeId = availableRouteIds[tabIndex];
      navigate(pages.students.path + '/' + routeId, {
        replace: true,
      });
    }
  };

  const handleFilterChange = (name: string, value: string) => {
    if (selectedTab === 0) {
      updateFilters({ [name]: value });
    }
  };

  if (!isDetailsRoute && availableRouteIds.length === 0) {
    return <Navigate to={safeLanding} replace />;
  }

  return (
    <PermissionGate
      module="students"
      checkAnySubmodule
      permission="read"
      fallback={<Navigate to={safeLanding} replace />}
    >
      <Grid container id="studentsViewContainer" sx={{ height: '100%' }}>
        <Grid
          item
          xs={12}
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', sm: 'row' },
            alignItems: { xs: 'stretch', sm: 'center' },
            justifyContent: 'space-between',
            pr: { xs: 0, sm: 4 },
            gap: { xs: 1, sm: 0 },
            mb: { xs: 1, sm: 0 },
          }}
          data-testid="students-view-header-container"
        >
          <MainTitle
            title={translateText('students.title')}
            subTitle={
              translateText(studentTabs[selectedTab]?.label ?? 'students.tabs.list')
            }
          />
          {filters.grade !== 'select' ? (
            <StudentListFilters
              onFilterChange={handleFilterChange}
              isPrimaryFilters={false}
              key={JSON.stringify(filters)}
            />
          ) : null}
        </Grid>
        <Grid item xs={12} sx={{ height: '100%', px: 0 }}>
          {filters.grade !== 'select' ? (
            <TabGroup
              key={selectedTab}
              tabs={studentTabs}
              initiallySelected={selectedTab}
              onTabChange={(value) => locationChangeHandler(value)}
              dataTestIdPrefix="students"
            />
          ) : (
            <StudentListFilters
              onFilterChange={handleFilterChange}
              isPrimaryFilters={true}
            />
          )}
        </Grid>
      </Grid>
    </PermissionGate>
  );
};

// Main component that provides the context
const StudentsView: FC<StudentsViewProps> = () => {
  const location = useLocation();
  const isDetailsRoute = location.pathname.includes('/details');

  // StudentsProvider/StudentListProvider are mounted above the route branch so
  // list selections and pagination survive navigating into (and back out of)
  // the details view. StudentFiltersProvider lives at the app root (src/index.tsx).
  return (
    <StudentsProvider>
      <StudentListProvider>
        {isDetailsRoute ? (
          // Details route just renders the outlet (StudentDetailsView has its own NavigationDrawer)
          <Outlet />
        ) : (
          <NavigationDrawer
            selectedPage={pages.students.path}
            window={window ?? null}
          >
            <Helmet>
              <title>{CONSTANTS.title.students}</title>
            </Helmet>
            <StudentsContent />
          </NavigationDrawer>
        )}
      </StudentListProvider>
    </StudentsProvider>
  );
};

export default StudentsView;
